/**
 * CreationShell + EventSummaryDock — ESM port.
 * The outer wizard frame: phase tracker, sub-step bar, summary dock,
 * live-lock / approval-pending banner, Back/Continue footer.
 */

import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { alpha, Btn, Icon, IconBtn, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { Screen } from "../../../shell/chrome";
import { useNav } from "../../../shell/router";
import { useCreationConfig, useCreationState, wizardFlowRoutes, isPublicAnyone, deriveEventIsLive, REPEAT_LABELS_ALL, type CreationConfig } from "./store";
import type { CreationOverrides } from "./store";
import { useEvents } from "../useEvents";
import { friendlyDate, friendlyDateTime, friendlyTimeRange } from "../../../lib/datetime";
import { useT } from "../../../lib/i18n";
import { useApprovals } from "../../approvals/approvals-store";

// ── EventSummaryDock ─────────────────────────────────────────
function SummaryRow({ label, value, route, hide }: { label: string; value?: string; route?: string; hide?: boolean }) {
  const t = useT();
  const nav = useNav();
  if (hide) return null;
  return (
    <Btn variant="ghost" type="button" onClick={() => route && nav.go(route)}
      style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%", height: "auto", whiteSpace: "normal", textAlign: "left", padding: "8px 10px", border: "none", borderRadius: 8, background: "transparent", cursor: route ? "pointer" : "default", fontFamily: T.font }}
      onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => { if (route) e.currentTarget.style.background = T.n6; }}
      onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = "transparent"; }}>
      <span style={{ marginTop: 2, color: value && value !== "Not set" ? T.green1 : T.n4, flexShrink: 0 }}>
        {value && value !== "Not set" ? <Icon.check size={13} /> : <span style={{ width: 13, height: 13, borderRadius: 999, border: `1.5px solid ${T.n4}`, display: "inline-block" }} />}
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04, fontWeight: 700 }}>{label}</span>
        <span style={{ display: "block", fontSize: 13, color: value && value !== "Not set" ? T.n1 : T.soft, marginTop: 4, wordBreak: "break-word" }}>{value || t("events.creationCreationShell.notSet", "Not set")}</span>
      </span>
    </Btn>
  );
}

function EventSummaryDock({ cfg, open, onToggle }: { cfg: CreationConfig & CreationOverrides; open: boolean; onToggle: () => void }) {
  const t = useT();
  const nav = useNav();
  const st = useCreationState();
  const tmpl = !!(st._templateMode);
  const tickets = (cfg.tickets || []) as Array<{ id: string; name: string; price?: number; kind?: string; color?: string }>;
  const groups = (cfg.ticketGroups || []) as Array<{ groupId: string; name: string; tiers?: unknown[] }>;
  const guestList = (cfg.guestList || []) as Array<{ kind?: string; count?: number }>;
  const guestCount = guestList.reduce((n, e) => n + (e.kind === "segment" ? (e.count || 0) : 1), 0);
  const repeatLabel = REPEAT_LABELS_ALL[cfg.repeatIndex || 0] || "Doesn't repeat";
  const money = (t: { kind?: string; price?: number }) => (t.kind === "free" || t.kind === "code" || !t.price) ? "Free" : `$${Number(t.price).toLocaleString()}`;
  const locLabel = cfg.location as string || "";

  // #797 — this dock renders beside EVERY wizard step; start/end are canonical
  // ISO, so hand-rendering them read as raw timestamps. Build the row through
  // lib/datetime (locale + venue-tz aware), e.g. "Tue, Sep 15, 2026 · 6:00 PM — 8:00 PM".
  const dockTz = ((String((cfg as { timezone?: string }).timezone || "").split(" ").pop()) || undefined) as string | undefined;
  const dateTimeLabel = (() => {
    if (!cfg.start) return undefined;
    const d0 = friendlyDate(cfg.start as string, dockTz);
    if (!cfg.end) return d0;
    const d1 = friendlyDate(cfg.end as string, dockTz);
    if (d0 === d1) return `${d0} · ${friendlyTimeRange(cfg.start as string, cfg.end as string, dockTz)}`;
    return `${friendlyDateTime(cfg.start as string, dockTz)} → ${friendlyDateTime(cfg.end as string, dockTz)}`;
  })();

  const TIPS: Record<string, string[]> = {
    "create.type": ["Pick the closest type — every default stays editable later.", "\"Build from scratch\" gives you a blank flow."],
    "create.basics": ["A clear title + hero image lift registrations the most.", "Visibility controls who can find and book the event."],
    "create.dates": ["Set the end time accurately — it drives reminders and auto-archiving.", "Turn on repeat to spin up a whole series at once."],
    "create.tickets": ["Start simple — you can add tiers or groups anytime.", "Capacity here feeds the registration snapshot."],
    "create.registration": ["Only ask for what you'll use — every field adds friction.", "Required questions block the registrant flow until answered."],
    "create.landing": ["Keep the title, date and a clear CTA above the fold."],
    "create.messaging": ["A library flow covers most events; customise per-event when needed."],
    "create.guests": ["Add people now, or invite more after publishing.", "Attach a segment to pull a list in one go."],
    "create.approvals": ["Submit early — approvers are emailed instantly.", "You can't publish until this is approved."],
    "create.publish": ["Publishing opens registration and starts comms immediately.", "Double-check the date, capacity and tickets below."],
    "create.live": ["Take the event offline to edit it again.", "Message guests by tier, group or individually from the Live tab."],
  };
  const tips = TIPS[nav.route] || [];

  if (!open) {
    return (
      <aside style={{ width: 40, flexShrink: 0, borderLeft: `1px solid ${T.border}`, background: T.white, display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 10, cursor: "pointer" }}
        onClick={onToggle} title={t("events.creationCreationShell.showEventSummary", "Show event summary")} role="button" aria-label={t("events.creationCreationShell.showEventSummary", "Show event summary")}>
        <span style={{ width: 28, height: 28, borderRadius: 6, display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.n2 }}><Icon.chev dir="left" size={14} /></span>
        <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", marginTop: 12, fontSize: 12, fontWeight: 700, color: T.n2, letterSpacing: 0.4, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Icon.list size={13} /> {tmpl ? t("events.creationShell.templateSummary", "Template summary") : t("events.creationCreateEventApprovals.eventSummary", "Event summary")}
        </div>
      </aside>
    );
  }

  return (
    <aside style={{ width: 312, flexShrink: 0, borderLeft: `1px solid ${T.border}`, background: T.white, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{tmpl ? t('events.creationCreationShell.text') : t('events.creationCreationShell.text2')}</div>
          <div style={{ fontSize: 11, color: T.soft, marginTop: 1 }}>{tmpl ? t('events.creationCreationShell.text3') : t('events.creationCreationShell.text4')}</div>
        </div>
        <IconBtn size={28} label={t("events.creationCreationShell.hideSummary", "Hide summary")} onClick={onToggle}><Icon.chev dir="right" size={14} /></IconBtn>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "8px 6px" }}>
        <div style={{ padding: "4px 10px 8px" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: cfg.title ? T.n1 : T.soft, lineHeight: 1.25 }}>{cfg.title || (tmpl ? "Untitled template" : "Untitled event")}</div>
          {cfg.label || cfg.typeName ? <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{cfg.label || (cfg as { typeName?: string }).typeName}</div> : null}
        </div>
        <SummaryRow label={t("events.creationCreationShell.format", "Format")} value={cfg.method ? (cfg.method.inPerson && cfg.method.virtual ? "Hybrid" : cfg.method.inPerson ? "In-person" : "Virtual") : undefined} route="create.details" />
        <SummaryRow label={t("events.creationCreationShell.visibility", "Visibility")} value={cfg.visibility || undefined} route="create.details" />
        <SummaryRow label={t("events.creationCreationShell.dateTime", "Date & time")} value={dateTimeLabel} route="create.dates" hide={tmpl} />
        <SummaryRow label={t("events.creationCreationShell.repeats", "Repeats")} value={repeatLabel} route="create.dates" hide={tmpl} />
        <SummaryRow label={t("events.creationCreationShell.location", "Location")} value={locLabel} route="create.details" />
        <SummaryRow label={t("events.creationCreationShell.capacity", "Capacity")} value={cfg.capacity ? `${Number(cfg.capacity).toLocaleString()} seats` : undefined} route="create.tickets" />
        <div style={{ padding: "8px 10px" }}>
          <div style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04, fontWeight: 700, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
            <span>{t("events.creationCreationShell.tickets", "Tickets")}</span>
            <Btn variant="link" type="button" onClick={() => nav.go("create.tickets")} style={{ border: "none", background: "none", color: T.p1, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: T.font, height: "auto", padding: 0 }}>{t("common.actions.edit", "Edit")}</Btn>
          </div>
          {(tickets.length || groups.length) ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {tickets.map((t) => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.n1 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: t.color || T.p1, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name || "Untitled"}</span>
                  <span style={{ color: T.soft }}>{money(t)}</span>
                </div>
              ))}
              {groups.map((g) => (
                <div key={g.groupId} style={{ fontSize: 12.5, color: T.n1, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Icon.layers size={11} /> {g.name} <span style={{ color: T.soft }}>· {(g.tiers || []).length} {t("events.creationCreationShell.tiers", "tiers")}</span>
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: 12.5, color: T.soft }}>{t("events.creationCreationShell.notSet", "Not set")}</div>}
        </div>
        <SummaryRow label={t("events.creationCreationShell.guestList", "Guest list")} value={guestCount ? `${guestCount.toLocaleString()} people` : undefined} route="create.guests" hide={tmpl || isPublicAnyone(cfg.visibility)} />
        {tips.length ? (
          <div style={{ margin: "8px 10px 4px", padding: 12, background: T.p4, border: `1px solid ${T.p2}`, borderRadius: 10 }}>
            <div style={{ fontSize: 11, color: T.p1, textTransform: "uppercase", letterSpacing: 0.04, fontWeight: 700, marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Icon.info size={12} /> {t("events.creationCreationShell.onThisStep", "On this step")}</div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
              {tips.map((t, i) => (
                <li key={i} style={{ display: "flex", gap: 8, fontSize: 12, color: T.n1, lineHeight: 1.45 }}>
                  <span style={{ color: T.p1, marginTop: 5, width: 4, height: 4, borderRadius: 999, background: T.p1, flexShrink: 0 }} /><span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

// ── CreationShell ────────────────────────────────────────────────────────────
export interface CreationShellProps {
  step?: number;
  children: ReactNode;
  footer?: ReactNode;
  nextLabel?: string;
  nextDisabled?: boolean;
  nextDisabledReason?: string;
}

export function CreationShell({ step = 0, children, footer, nextLabel = "Continue", nextDisabled = false, nextDisabledReason = "" }: CreationShellProps) {
  const t = useT();
  const nav = useNav();
  const cfg = useCreationConfig();
  const state = useCreationState();
  const tmpl = !!(state._templateMode);
  const skipGuests = isPublicAnyone(cfg.visibility);
  // Addendum item 14 / UIR-30: the approvals phase exists when the tenant has
  // an enabled events approval process (real store, hydrated from the ops) —
  // never a hardcoded flag.
  const approvalsStoreForFlow = useApprovals();
  const approvalsOn = !tmpl && !!approvalsStoreForFlow.enabledTypes().events && approvalsStoreForFlow.processes("events").length > 0;
  const isEditing = !!(state._editingId);
  const cfgRef = useRef(cfg); cfgRef.current = cfg;
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(() => { try { return localStorage.getItem("jrni_wizard_summary") !== "0"; } catch { return true; } });
  const toggleSummary = () => setSummaryOpen((v) => { const n = !v; try { localStorage.setItem("jrni_wizard_summary", n ? "1" : "0"); } catch { /* noop */ } return n; });

  const ALL_PHASES = [
    { id: 0, label: t("events.creationCreationShell.basics", "Basics"), route: "create.basics" },
    { id: 1, label: t("events.creationCreationShell.ticketsRegistration", "Tickets & registration"), route: "create.tickets" },
    { id: 2, label: t("events.creationCreationShell.landingPage", "Landing page"), route: "create.landing" },
    { id: 2.5, label: t("events.creationCreationShell.messaging", "Messaging"), route: "create.messaging" },
    { id: 3, label: t("events.creationCreationShell.guestList", "Guest list"), route: "create.guests" },
    { id: 4, label: t("events.creationCreationShell.reviewPublish", "Review & publish"), route: "create.publish" },
    { id: 5, label: t("common.status.live", "Live"), route: "create.live" },
  ];
  const PHASES = ALL_PHASES
    .filter((p) => { if (tmpl) return p.id !== 3 && p.id !== 5; return p.id !== 3 || !skipGuests; })
    .map((p) => (tmpl && p.id === 4) ? { ...p, label: t("events.creationCreationShell.saveTemplate", "Save template") } : p);

  const ROUTE_PHASE: Record<string, number> = {
    "create.type": 0, "create.basics": 0, "create.details": 0, "create.dates": 0,
    "create.tickets": 1, "create.registration": 1,
    "create.landing": 2, "create.messaging": 2.5, "create.guests": 3, "create.approvals": 4, "create.publish": 4, "create.live": 5,
  };
  const PHASE_SUBSTEPS: Record<number, { label: string; route: string }[]> = {
    0: tmpl
      ? [{ label: t("events.creationCreationShell.essentials", "Essentials"), route: "create.basics" }, { label: t("events.creationCreationShell.details", "Details"), route: "create.details" }]
      : [{ label: t("events.creationCreationShell.essentials", "Essentials"), route: "create.basics" }, { label: t("events.creationCreationShell.details", "Details"), route: "create.details" }, { label: t("events.creationCreationShell.schedule", "Schedule"), route: "create.dates" }],
    1: [{ label: t("events.creationCreationShell.tickets", "Tickets"), route: "create.tickets" }, { label: t("events.creationCreationShell.registration", "Registration"), route: "create.registration" }],
    2: [{ label: t("events.creationCreationShell.landingPage", "Landing page"), route: "create.landing" }],
    2.5: [{ label: t("events.creationCreationShell.messaging", "Messaging"), route: "create.messaging" }],
    3: [{ label: t("events.creationCreationShell.guestList", "Guest list"), route: "create.guests" }],
    4: [{ label: t("events.creationCreationShell.reviewPublish", "Review & publish"), route: "create.publish" }],
    5: [{ label: t("common.status.live", "Live"), route: "create.live" }],
  };

  const FLOW = wizardFlowRoutes(cfg, tmpl);
  const curRoute = FLOW.includes(nav.route) ? nav.route : (FLOW[0] || nav.route);
  const curIdx = Math.max(0, FLOW.indexOf(curRoute));
  const nextRoute = FLOW[curIdx + 1] || "events";
  const backRoute = curIdx > 0 ? FLOW[curIdx - 1] : "events";
  const phase = ROUTE_PHASE[nav.route] ?? 0;
  // ── Live lock ──────────────────────────────────────────────────────────────
  // An event is "live" when it's been published this session (_published flag)
  // OR the stored event we're editing has a live/upcoming (published) status.
  // While live, all editing steps are locked; the Live tab stays interactive so
  // the user can message guests / take it offline.
  const { events } = useEvents();
  const editingId = state._editingId as string | undefined;
  const storedEvent = editingId ? events.find((e) => e.id === editingId) : null;
  // 2026-07-31: the lock is gone — a live event stays fully editable. `eventIsLive`
  // now only drives the informational banner below, never pointer-events/opacity.
  const eventIsLive = !tmpl && deriveEventIsLive(cfg, storedEvent);
  const showLiveBanner = eventIsLive && nav.route !== "create.live";

  return (
    <Screen rail="events" topbar>
      {/* Header */}
      <div style={{ padding: "16px 24px", background: T.white, borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <h1 style={{ ...tx.h3, fontWeight: 600 }}>{tmpl ? (state._templateId ? "Edit Event Template" : "Create Event Template") : (isEditing ? "Edit event" : "Create event")}</h1>
          <Pill tone={(tmpl || isEditing) ? "primary" : "neutral"}>{tmpl ? (cfg.title || "Untitled template") : (isEditing ? (cfg.title || "Untitled event") : "Untitled draft")}</Pill>
          <span style={{ fontSize: 12, color: T.soft, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: T.green1 }} />
            {tmpl ? t('events.creationCreationShell.text5') : (savedAt ? "Saved · just now" : "Autosaves as you go")}
          </span>
        </div>
        {(nav.params?.editing || nav.params?.eventId) ? (
          <Btn variant="ghost" size="sm" icon={<Icon.eye size={14} />} onClick={() => nav.go("event.public", { eventId: nav.params?.editing || nav.params?.eventId })}>{t("events.creationCreationShell.preview", "Preview")}</Btn>
        ) : null}
      </div>

      {/* Phase tracker */}
      <div style={{ padding: "16px 24px", background: T.white, borderBottom: `1px solid ${T.border}` }}>
        <style dangerouslySetInnerHTML={{ __html: "@media (max-width: 1180px){.wiz-phase:not(.is-here) .wiz-phase-label{display:none}}@media (max-width: 880px){.wiz-phase .wiz-phase-label{display:none}}" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
          {PHASES.map((p, i) => {
            const done = p.id < phase; const here = p.id === phase;
            return (
              <div key={p.label} style={{ display: "contents" }}>
                <div className={`wiz-phase${here ? " is-here" : ""}`} onClick={() => nav.go(p.route)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 999, background: done ? T.p1 : T.white, color: done ? T.white : here ? T.p1 : T.n3, border: done ? "none" : `1.5px solid ${here ? T.p1 : T.n4}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>
                    {done ? <Icon.check size={12} /> : i + 1}
                  </span>
                  <span className="wiz-phase-label" style={{ fontSize: 13, fontWeight: here ? 600 : 500, color: here ? T.n1 : done ? T.n1 : T.soft, whiteSpace: "nowrap" }}>{p.label}</span>
                </div>
                {i < PHASES.length - 1 ? <span style={{ flex: 1, minWidth: 12, height: 1, background: done ? T.p1 : T.n4, margin: "0 10px" }} /> : null}
              </div>
            );
          })}
        </div>
        {(PHASE_SUBSTEPS[phase] || []).length > 1 ? (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.n6}`, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.04, marginRight: 4 }}>{(PHASES.find((p) => p.id === phase) || {}).label}</span>
            {PHASE_SUBSTEPS[phase].map((s, i) => {
              const active = nav.route === s.route;
              return (
                <div key={s.route} style={{ display: "contents" }}>
                  <Btn variant="ghost" type="button" onClick={() => nav.go(s.route)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 12px 5px 6px", borderRadius: 999, border: `1px solid ${active ? T.p1 : T.n4}`, background: active ? T.p4 : T.white, color: active ? T.p1 : T.n2, fontFamily: T.font, fontSize: 12.5, fontWeight: 600, cursor: "pointer", height: "auto", whiteSpace: "normal" }}>
                    <span style={{ width: 18, height: 18, borderRadius: 999, background: active ? T.p1 : T.n6, color: active ? T.white : T.soft, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{i + 1}</span>
                    {s.label}
                  </Btn>
                  {i < PHASE_SUBSTEPS[phase].length - 1 ? <Icon.chev dir="right" size={12} /> : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "hidden", background: T.canvas, display: "flex" }}>
        <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
          {showLiveBanner ? (
            <Btn variant="ghost" type="button" onClick={() => nav.go("create.live")}
              style={{ margin: "16px 24px 0", width: "calc(100% - 48px)", display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", border: "none", borderRadius: 0, borderTop: `1px solid ${T.green2}`, borderBottom: `1px solid ${T.green2}`, background: T.green2, color: T.green1, fontFamily: T.font, fontSize: 12.5, fontWeight: 600, cursor: "pointer", flexShrink: 0, textAlign: "left", height: "auto", whiteSpace: "normal" }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: T.green1, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{t("events.creationCreationShell.thisEventIsLiveActive", "This event is live — changes you publish here will update it immediately")}</span>
              <Icon.chev dir="right" size={13} />
            </Btn>
          ) : null}
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {children}
          </div>
        </div>
        <EventSummaryDock cfg={cfg} open={summaryOpen} onToggle={toggleSummary} />
      </div>

      {/* Footer */}
      <div style={{ padding: "12px 24px", background: T.white, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {footer || (
          <>
            <Btn variant="secondary" icon={<Icon.arrow dir="left" size={14} />} onClick={() => nav.go(backRoute)}>{t("common.actions.back", "Back")}</Btn>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {nextDisabled && nextDisabledReason ? <span style={{ fontSize: 12, color: T.soft, display: "inline-flex", alignItems: "center", gap: 6 }}><Icon.info size={12} /> {nextDisabledReason}</span> : null}
              <Btn iconRight={<Icon.arrow size={14} />} disabled={nextDisabled} title={nextDisabled ? (nextDisabledReason || t("events.creationShell.completeRequiredFieldsToContinue", "Complete required fields to continue")) : undefined} onClick={() => nav.go(nextRoute)}>{nextLabel}</Btn>
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}
